import React, { useState, useCallback } from 'react';
import { Layout, Menu, Avatar, Space, Tooltip, Button } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGrid,
  faTableColumns,
  faBug,
  faUser,
  faChartLine,
  faCrown,
  faArrowRightFromBracket,
  faSidebar,
  faBorderLeft,
  faFileLines,
  faRocketLaunch,
  faCommentDots,
  faClipboardList,
} from '@fortawesome/pro-solid-svg-icons';
import { textStyles } from '../../utils/textStyles';
import { useAuthStore } from '../../store/authStore';
import NotificationBell from '../notifications/NotificationBell';
import { colors } from '../../theme/colors';
import '../../styles/fonts.css';

const SIDER_WIDTH = 200;
const SIDER_COLLAPSED_WIDTH = 80;
const SIDER_PADDING = 16;
const HEADER_HEIGHT = 64;
const LAYOUT_COLLAPSED_KEY = 'ourora-core-sider-collapsed';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const [siderCollapsed, setSiderCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(LAYOUT_COLLAPSED_KEY);
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });

  const setCollapsed = useCallback((collapsed: boolean) => {
    setSiderCollapsed(collapsed);
    try {
      localStorage.setItem(LAYOUT_COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, []);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <FontAwesomeIcon icon={faGrid} />,
      label: 'Dashboard',
      title: 'Dashboard',
    },
    {
      key: '/planner',
      icon: <FontAwesomeIcon icon={faTableColumns} />,
      label: 'Planner',
      title: 'Planner',
    },
    {
      key: '/queue',
      icon: <FontAwesomeIcon icon={faBug} />,
      label: 'Ops Queue',
      title: 'Ops Queue',
    },
    {
      key: '/notes',
      icon: <FontAwesomeIcon icon={faFileLines} />,
      label: 'Notes',
      title: 'Notes',
    },
    {
      key: '/analytics',
      icon: <FontAwesomeIcon icon={faChartLine} />,
      label: 'Analytics',
      title: 'Analytics',
    },
    {
      key: '/profile',
      icon: <FontAwesomeIcon icon={faUser} />,
      label: 'Profile',
      title: 'Profile',
    },
    ...(user?.role === 'ADMIN'
      ? [
          {
            key: '/releases',
            icon: <FontAwesomeIcon icon={faRocketLaunch} />,
            label: 'Releases',
            title: 'Releases',
          },
          {
            key: '/user-feedback',
            icon: <FontAwesomeIcon icon={faCommentDots} />,
            label: 'User Feedback',
            title: 'User Feedback',
          },
          {
            key: '/discuss',
            icon: <FontAwesomeIcon icon={faClipboardList} />,
            label: 'Discuss',
            title: 'Discuss',
          },
          {
            key: '/admin',
            icon: <FontAwesomeIcon icon={faCrown} />,
            label: 'Admin',
            title: 'Admin',
          },
        ]
      : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const siderWidth = siderCollapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;
  const pageTitle = menuItems.find((item) => item.key === location.pathname)?.label ?? 'Ourora Core';

  return (
    <Layout style={{ minHeight: '100vh', background: colors.bgBase, flexDirection: 'column' }}>
      {/* Full-width header at top */}
      <Header
        style={{
          background: colors.bgBase,
          borderBottom: `1px solid ${colors.border}`,
          padding: `0 ${SIDER_PADDING}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          width: '100%',
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
          <img
            src="/assets/ourora_logo.png"
            alt="Ourora"
            style={{ height: '32px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
          />
          <span style={{ ...textStyles.title2Bold, color: colors.white, flexShrink: 0 }}>CORE</span>
          <span style={{ color: colors.textMuted, fontSize: 14, flexShrink: 0 }}>|</span>
          <span
            style={{
              ...textStyles.headlineBold,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {pageTitle}
          </span>
        </div>
        <Space size={16} align="center" style={{ flexShrink: 0 }}>
          <NotificationBell />
          {user?.photoUrl && (
            <Avatar
              src={user.photoUrl}
              size={36}
              style={{ cursor: 'pointer', border: `2px solid ${colors.border}` }}
              onClick={() => navigate('/profile')}
            />
          )}
        </Space>
      </Header>

      {/* Below header: nav (left) + body (right) */}
      <Layout style={{ flex: 1, display: 'flex', position: 'relative', minHeight: 0 }}>
        <Sider
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          collapsed={siderCollapsed}
          collapsible
          trigger={null}
          style={{
            background: colors.bgBase,
            borderRight: `1px solid ${colors.border}`,
            overflow: 'auto',
            position: 'fixed',
            left: 0,
            top: HEADER_HEIGHT,
            bottom: 0,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
            <div style={{ padding: `${SIDER_PADDING}px ${SIDER_PADDING}px 0` }}>
              <Menu
                mode="inline"
                inlineCollapsed={siderCollapsed}
                selectedKeys={[location.pathname]}
                items={menuItems}
                onClick={handleMenuClick}
                className="main-layout-nav"
                style={{
                  background: 'transparent',
                  color: colors.textPrimary,
                  border: 'none',
                  marginTop: 8,
                }}
              />
            </div>

            <div
              style={{
                padding: SIDER_PADDING,
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
              }}
            >
              <Tooltip title={siderCollapsed ? 'Expand' : 'Collapse'} placement="right">
                <Button
                  type="text"
                  icon={
                    <FontAwesomeIcon
                      icon={siderCollapsed ? faSidebar : faBorderLeft}
                      style={{ fontSize: '14px' }}
                    />
                  }
                  onClick={() => setCollapsed(!siderCollapsed)}
                  style={{
                    color: colors.textPrimary,
                    width: '100%',
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: siderCollapsed ? 'center' : 'flex-start',
                    paddingLeft: siderCollapsed ? 0 : SIDER_PADDING + 8,
                  }}
                >
                  {!siderCollapsed && 'Collapse'}
                </Button>
              </Tooltip>
              <div
                style={{
                  padding: siderCollapsed ? '8px 0' : '16px 0 0 0',
                  paddingLeft: siderCollapsed ? 0 : SIDER_PADDING + 8,
                  cursor: 'pointer',
                  transition: 'background 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: siderCollapsed ? 'center' : 'flex-start',
                }}
                onClick={handleLogout}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 77, 79, 0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <FontAwesomeIcon
                  icon={faArrowRightFromBracket}
                  style={{ fontSize: '14px', marginRight: siderCollapsed ? 0 : 10, color: colors.error }}
                />
                {!siderCollapsed && <span style={{ color: colors.error }}>Logout</span>}
              </div>
            </div>
          </div>
        </Sider>
        <Content
          style={{
            background: colors.bgBase,
            overflow: 'initial',
            marginLeft: siderWidth,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
